import {
  AloftArea,
  AloftPrice,
  CraftsmanCostProps,
  getCraftsmanCost,
  isUndefined,
} from '@aloft/toolkit-shared';

import { selectEffectiveDate } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';
import { selectFoundationCostOutput } from './selectFoundationCost.ts';

const calculateManufacturedCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];

  if (isUndefined(improvementsForTransaction) || isUndefined(effectiveDate)) {
    return undefined;
  }

  const { gla, craftsmanQuality: quality } = improvementsForTransaction;

  if (
    isUndefined(gla) ||
    isUndefined(quality) ||
    (quality * 10) % 1 !== 0 ||
    quality < 1 ||
    quality > 5
  ) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  return calculator.getManufacturedCost({
    quality,
    sqft: AloftArea.schema.parse(gla),
  });
};

const getTotalOrBreakdownManufacturedCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  foundationCostOutput: ReturnType<typeof selectFoundationCostOutput>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  id: string,
  type: 'totalCost' | 'zipOnly' | 'none',
) => {
  if (improvements[id]?.propertyType !== 'manufacturedHome') {
    return undefined;
  }
  const costOutput = calculateManufacturedCost(costProps, improvements, effectiveDate, id);

  if (costOutput === undefined) {
    return AloftPrice.zero;
  }

  let total: AloftPrice;

  if (type === 'totalCost') {
    const totalWithoutFoundation = costOutput.totalCost;
    total = totalWithoutFoundation.add(foundationCostOutput?.totalCost);
  } else {
    const totalWithoutFoundation = costOutput.multiplierBreakdown[type];
    total = totalWithoutFoundation.add(foundationCostOutput?.multiplierBreakdown[type]);
  }

  return total;
};

const getTotalManufacturedCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  foundationCostOutput: ReturnType<typeof selectFoundationCostOutput>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  id: string,
) =>
  getTotalOrBreakdownManufacturedCost(
    costProps,
    improvements,
    foundationCostOutput,
    effectiveDate,
    id,
    'totalCost',
  );

const getBaseManufacturedCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  foundationCostOutput: ReturnType<typeof selectFoundationCostOutput>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  id: string,
) =>
  getTotalOrBreakdownManufacturedCost(
    costProps,
    improvements,
    foundationCostOutput,
    effectiveDate,
    id,
    'none',
  );

const getManufacturedGlaCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  id: string,
) => {
  const costOutput = calculateManufacturedCost(costProps, improvements, effectiveDate, id);
  const gla = improvements[id]?.gla;

  if (costOutput === undefined || costOutput.variable === undefined || gla === undefined) {
    return AloftPrice.zero;
  }

  const parsedGla = AloftArea.fromNumber(gla.unitAmount, gla.unit);

  return costOutput.variable.mul(parsedGla.getUnitAmount('sqft'));
};

const getManufacturedFixedCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  id: string,
) => {
  const costOutput = calculateManufacturedCost(costProps, improvements, effectiveDate, id);

  if (costOutput === undefined || costOutput.fixed === undefined) {
    return AloftPrice.zero;
  }

  return costOutput.fixed;
};

const selectManufacturedGlaCost = createPriceSelector(
  [selectCostProps, selectImprovementsEntities, selectEffectiveDate, (_state, id: string) => id],
  getManufacturedGlaCost,
);

const selectManufacturedFixedCost = createPriceSelector(
  [selectCostProps, selectImprovementsEntities, selectEffectiveDate, (_state, id: string) => id],
  getManufacturedFixedCost,
);

const selectManufacturedGlaAndFixedCost = createPriceSelector(
  [selectManufacturedGlaCost, selectManufacturedFixedCost],
  (glaCost, fixedCost) => {
    if (isUndefined(glaCost) || isUndefined(fixedCost)) {
      return undefined;
    }

    return glaCost.add(fixedCost);
  },
);

export const selectManufacturedGlaAndFixedCostDisplay = createPriceSelector(
  [selectManufacturedGlaAndFixedCost],
  (cost) => {
    if (isUndefined(cost)) {
      return undefined;
    }

    return cost.toString(true);
  },
);

export const selectManufacturedCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectFoundationCostOutput,
    selectEffectiveDate,
    (_state, id: string) => id,
  ],
  getTotalManufacturedCost,
);

export const selectBaseManufacturedCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectFoundationCostOutput,
    selectEffectiveDate,
    (_state, id: string) => id,
  ],
  getBaseManufacturedCost,
);

export const selectManufacturedCostOutput = createPriceSelector(
  [selectCostProps, selectImprovementsEntities, selectEffectiveDate, (_state, id: string) => id],
  calculateManufacturedCost,
);
